import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { useMe } from './facts.js';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { pacificClock } from '@claims/core';
import { badgeOf, BADGE_MAX, countOf } from './count.js';
import { Ic } from './icons.js';
import { toggleTheme } from './prefs.js';
import { AccountScreen, accountState, STATE_WORDS } from './states.js';
import { TextSizeControl } from './TextSizeControl.js';
import { usePress } from './press.js';
import { PressButton } from './press-button.js';

// The frame in the medrec console's look (2026-10-11, Rod: "both systems stay in theme"; its 10-17 redesign since 2026-10-18, DESIGN_SYSTEM
// 6): the rail starts with the logo block (the product mark left the top bar for it), then an emoji over one word for each page, grey until
// the page is current or pointed at, and the signed-in person's initials at its foot; the 56 px top bar holds the page title with its count
// and "updated" time, the mode pill, the search, then the same tools on every page (SVG line icons, the one place this console does not use
// emoji; the text size tool's "Aa" is letters) and the Pacific clock.

function NavItem({ to, emoji, label, count, hot, title }: { to: string; emoji: string; label: string; count?: number | null; hot?: boolean; title?: string }) {
  return (
    <NavLink className={({ isActive }) => `ni${isActive ? ' on' : ''}`} to={to} title={title ?? label}>
      <span className="emo" aria-hidden="true">{emoji}</span><span className="nl">{label}</span>
      {/* past 999 the badge reads "999+", set against the rail's edge so four characters never run past it (the medrec console's PR #177,
          A7); the hover keeps the whole count */}
      {count ? <span className={`nc${hot ? ' hot' : ''}${count > BADGE_MAX ? ' wide' : ''}`} data-testid={`nav-${label.toLowerCase()}-count`}>{badgeOf(count)}</span> : null}
    </NavLink>
  );
}

const ROLE: Record<string, string> = { 'claims-admin': 'admin', 'claims-team': 'claims team' };
function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return `${parts[0]![0]!}${parts.length > 1 ? parts[parts.length - 1]![0]! : ''}`.toUpperCase();
}
function Me() {
  const me = useMe();
  const name = me.data?.name ?? null;
  const role = me.data?.roles.map((r) => ROLE[r] ?? r).join(', ') || null;
  const who = [name, role].filter(Boolean).join(' · ') || 'Signed in';
  return <span className="avatar" title={who}><span aria-hidden="true">{initials(name)}</span><span className="sr">{who}</span></span>;
}

/** The product's name and what it is for: the rail's logo block (its hover and its name). */
export const PRODUCT = Object.freeze({
  name: 'Sweet Claims',
  hover: "Sweet Claims: the claims team's console (claims now; case research from vendors like Coastal Research and LexisNexis later)",
});

export function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  // the rail's badge on every page, as in the medrec console (its own tiny answer): the client replies waiting for a person (Kimi's layout
  // pass 2026-10-13: the group every tab shows first); the urgent count is in the hover
  const issues = useQuery({ queryKey: ['issuesCount'], queryFn: api.issuesCount, staleTime: 60_000, refetchInterval: 120_000 });
  const replies = issues.data?.replies ?? null;
  const urgent = issues.data?.urgent ?? null;
  const tip = issues.data ? `Queue: ${countOf(replies ?? 0)} client repl${replies === 1 ? 'y' : 'ies'} waiting${urgent !== null ? ` · ${countOf(urgent)} urgent rows` : ''}` : 'Queue';
  // the account's own failure (no claims console role, or the sign-in ran out): the sign-in box, not a frame of pages that cannot load
  const account = accountState(me.error);
  if (account) return <AccountScreen state={account} />;
  // THE RAIL IN ITS FIXED ORDER (the medrec console's DESIGN_SYSTEM 6): the logo block, then the work pages, then the initials. The medrec
  // console has a divider before Admin and Settings; Sweet Claims has neither page, so no divider (the old one after the Queue went too:
  // every item is a page a person works from).
  return (
    <div className="app">
      <nav className="nav" aria-label="Claims console">
        <Link className="nav-logo" to="/matters" title={PRODUCT.hover} aria-label={PRODUCT.name} data-testid="nav-logo">
          <img src="/brand/sweetclaims-logo-180.png" alt="" width={40} height={40} />
        </Link>
        <NavItem to="/matters" emoji="📋" label="Queue" count={replies} hot title={tip} />
        <NavItem to="/today" emoji="☎️" label="Today" />
        <NavItem to="/state-farm" emoji="📝" label="State Farm" />
        <NavItem to="/trends" emoji="📈" label="Trends" />
        <div className="nfoot"><Me /></div>
      </nav>
      <div className="main">{children}</div>
    </div>
  );
}

/** What a press or a typed fact does in this mode, said once in the header (the medrec console's header pill), the full sentence on hover. */
function ModePill() {
  const me = useMe();
  if (!me.data) return null;
  const [cls, emoji, short, full] = me.data.writeBack
    ? ['wb', '✏️', 'Typing saves to Salesforce', `Live data. What you type in a row (claim #, policy #, phone, carrier, no insurance) is written to Salesforce by the system within 15 minutes, with your name on it.${me.data.lorSend ? ' Send LOR sends the letter.' : ''} ${me.data.dryRun ? 'Other buttons are dry runs.' : 'Other buttons are off.'}`]
    : me.data.dryRun ? ['dry', '🧪', 'Buttons are dry runs', 'Live data. Buttons are dry runs: each press says what the controller would do and is recorded; nothing is written to Salesforce.']
      : ['ro', '👁️', 'View only', 'Live data, view only. Nothing on this page writes to Salesforce; open the case in Salesforce to change it.'];
  return <span className={`mode ${cls}`} title={full}><span className="emo" aria-hidden="true">{emoji}</span><span className="mode-t">{short}</span><span className="sr">{full}</span></span>;
}

/** THE TOP BAR'S SEARCH ON EVERY LIST PAGE (ported 2026-10-18 from the medrec console's FRAME_SEARCH, lib/console-frame.ts, its redesign
 *  follow-ups, PR #166; DESIGN_SYSTEM 6: search in every page's top bar). One control (Topbar's), on every page, with what that page searches
 *  in its own words; every page keeps the rows that hold every typed word by one rule (core list_search.ts), All unopened on the server, and
 *  says so in one line when none does (core SEARCH_WORDS.none). */
export const FRAME_SEARCH = Object.freeze({
  queue: 'Search client, DOL, carrier, SI',
  unopened: 'Search client, DOL, carrier, matter',
  allCases: 'Search client, DOL, carrier, case manager',
  case: 'Search this case',
  today: 'Search client, carrier, CAN, who',
  stateFarm: 'Search client or matter',
  trends: 'Filter a task, source, reason or carrier',
});

/** THE TOP BAR (the medrec console's DESIGN_SYSTEM 6): the title (h1) and its grey line, the mode pill, the search, then Text size, Theme,
 *  Refresh and the Pacific clock. `stale`: the page's last read failed while an earlier answer is on screen (TanStack's `isRefetchError`):
 *  the line drops "updated", and the clock stops amber on the last good time ("Not updated since 9:12 am PT") beside Try again (never "Retry": the medrec console's
 *  redesign follow-ups, its PR #166, 2026-10-18; DESIGN_SYSTEM 12). */
export function Topbar({ title, sub, asOf, q, setQ, ph, stale = false, onRetry }: { title: string; sub: string; asOf?: string | null; q: string; setQ: (v: string) => void; ph: string;
  stale?: boolean; onRetry?: () => unknown }) {
  const qc = useQueryClient();
  const [dark, setDark] = useState(document.documentElement.getAttribute('data-theme') === 'dark');
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => { const t = setInterval(() => setNow(new Date().toISOString()), 30_000); return () => clearInterval(t); }, []);
  // "/" focuses the search, as in the medrec console (not while typing in another field)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)))) return;
      const box = document.querySelector<HTMLInputElement>('.top .search input');
      if (box) { e.preventDefault(); box.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const line = [sub, asOf && !stale ? `updated ${pacificClock(asOf)} PT` : null].filter(Boolean).join(' · ');
  const retry = onRetry ?? (() => qc.invalidateQueries());
  // REFRESH SAYS IT IS READING (ported 2026-10-18 from the medrec console's speed-1018 lane, its PR #176): while a Refresh the person pressed
  // is out, its arrows turn from 150 ms in (a fast read never turns) and its hover says so; never on a page's own poll. Not disabled, so the
  // focus stays; a second press while one is out reads nothing more (press.ts).
  const refresh = usePress(() => qc.invalidateQueries());
  return (
    <header className="top">
      <div className="ttl"><h1 title={title}>{title}</h1>{line ? <span className="sub" title={line}>{line}</span> : null}</div>
      <ModePill />
      <label className="search"><Ic name="search" /><input type="search" placeholder={ph} aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} /><kbd title="Press / to search">/</kbd></label>
      <div className="tools">
        {/* the text size (ported 2026-10-12 from the medrec console, Rod's pick C there): beside the theme toggle, as there */}
        <TextSizeControl />
        <button type="button" className="iconbtn" aria-label={dark ? 'Light theme' : 'Dark theme'} title={dark ? 'Light theme' : 'Dark theme'} onClick={() => setDark(toggleTheme() === 'dark')}><Ic name={dark ? 'sun' : 'moon'} /></button>
        <button type="button" className="iconbtn" aria-label="Refresh" title={refresh.busy ? 'Reading the page again' : 'Refresh'} aria-busy={refresh.busy ? true : undefined}
          data-busy={refresh.busy ? 'true' : undefined} onClick={() => { void refresh.run(); }}><Ic name="refresh" /></button>
        {stale ? (
          <>
            <span className="clock stale" title="The last read failed; this page shows the answer from that time. Nothing changed.">{asOf ? `Not updated since ${pacificClock(asOf)} PT` : 'Not loaded yet'}</span>
            <PressButton label={STATE_WORDS.tryAgain} className="clock-retry" title="Read this page again now" onPress={retry} />
          </>
        ) : <span className="clock">{pacificClock(now)} PT</span>}
      </div>
    </header>
  );
}
